import { useState, useEffect, useRef, useCallback } from 'react';
import { usePageSearch } from './usePageSearch';
import { FilterValues, applyFilters } from '../components/TableFilter';

interface UseOptimizedPageSearchProps<T> {
  data: T[];
  searchFields: string[];
  initialFilters?: FilterValues;
}

export const useOptimizedPageSearch = <T extends Record<string, any>>({
  data,
  searchFields,
  initialFilters = {}
}: UseOptimizedPageSearchProps<T>) => {
  const [filteredData, setFilteredData] = useState<T[]>([]);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);

  // Références pour éviter les re-renders
  const dataRef = useRef<T[]>([]);
  const filtersRef = useRef<FilterValues>({});

  // Mettre à jour les références
  useEffect(() => {
    dataRef.current = data;
    filtersRef.current = filters;
  }, [data, filters]);

  // Initialiser les données filtrées au premier chargement et quand les filtres changent
  useEffect(() => {
    if (data.length > 0) {
      const initialFiltered = applyFilters(data, filters);
      setFilteredData(initialFiltered);
    } else {
      setFilteredData([]);
    }
  }, [data, filters]);

  // Configuration de la recherche globale optimisée
  const handleGlobalSearch = useCallback((term: string, filtered: any[]) => {
    if (term && term.trim()) {
      // Appliquer les filtres de table aux résultats de recherche
      const finalFiltered = applyFilters(filtered || [], filters);
      setFilteredData(finalFiltered);
    } else {
      // Si pas de recherche, utiliser les filtres de table seulement
      const currentFiltered = applyFilters(data, filters);
      setFilteredData(currentFiltered);
    }
  }, [data, filters]);

  // Utiliser le hook de recherche globale
  usePageSearch({
    data,
    searchFields,
    onSearch: handleGlobalSearch
  });

  // Gestionnaire de changement de filtres optimisé
  const handleFilterChange = useCallback((newFilters: FilterValues) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
  }, []);

  return {
    filteredData,
    filters,
    handleFilterChange,
    handleClearFilters
  };
};
